/*
 * Copyright (c) 2020, WSO2 Inc. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 Inc. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { IntlProvider } from "react-intl";
import { Switch, Route, Redirect, useHistory } from "react-router-dom";
import ApiPage from "./components/ApiPage";
import AppPage from "./components/AppPage";
import AppList from "./components/home/AppList";
import SignIn from "./components/signin";
import "./App.css";
import Login from "./components/login";
import PageNotFound from "./components/shared/PageNotFound";
import ApiListPage from "./components/ApiListPage";
import DevportalHome from "./components/home/DevportalHome";
import MainAppBar from "./components/shared/MainAppBar";
import InternalServerError from "./components/shared/InternalServerError";
import Unauthorized from "./components/shared/Unauthorized";
import { ThemeProvider } from "@material-ui/styles";
import { Box, Button } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { Theme } from "@material-ui/core/styles";
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { SnackbarProvider } from "notistack";
import axios from "axios";
import { systemTheme } from "./styles";
import { getAzureCDNEndpoint, localStoreOperation } from "./data/utils";
import { RESOURCE_SIGN_IN, SELECTED_ORG_HANDLE_NAME } from "./data/constants";
import {
  ApplicationState,
  CustomDomainOrganizationMappingReducer,
  OrganizationMetadataReducer,
  StorageOperations,
  UserReducer,
} from "./data/interfaces";
import AcceptInvitation from "./components/invitation/AcceptInvitation";
import ClientError from "./components/shared/ClientError";
import Settings from "./conf/settings";
import { fetchCustomDomainOrganizationMapping, fetchOrganizationMetadata } from "./data/operations";
import { PreLoaderLogo } from "./elements/PreLoaderLogo";
import makeStyles from "@material-ui/core/styles/makeStyles";
import { useAuthContext } from "@asgardeo/auth-react";
import Forbidden from "./components/shared/Forbidden";

const useStyles = makeStyles((theme) => ({
  loaderContainer: {
    width: "cal(100%-75px)",
    height: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#f7f8fb",
  },
}));

const language =
  (navigator.languages && navigator.languages[0]) || navigator.language;
const languageWithoutRegionCode = language.toLowerCase().split(/[_-]+/)[0];

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: false, refetchOnWindowFocus: false } },
});

export interface AppProps {
  userReducer: UserReducer;
  organizationMetadataReducer: OrganizationMetadataReducer;
  customDomainOrganizationMappingReducer: CustomDomainOrganizationMappingReducer;
  fetchCustomDomainOrganizationMapping: (customDomain: string) => Promise<void>;
  fetchOrganizationMetadata: (orgHandle?: string, customDomain?: string) => Promise<void>;
}

function App(props: AppProps) {
  const classes = useStyles();
  const history = useHistory();
  const { getBasicUserInfo } = useAuthContext();
  const {
    userReducer,
    organizationMetadataReducer,
    customDomainOrganizationMappingReducer,
    fetchCustomDomainOrganizationMapping,
    fetchOrganizationMetadata,
  } = props;

  const extractOrgHandleFromPath = (
    path: string | null | undefined
  ): string => {
    if (!path) {
      return "";
    }
    const resources = ["sign-in", "client-error", "forbidden", "login"];
    const result = path.match("^/([^/]+)");
    if (result && result.length > 0) {
      if (resources.includes(result[1])) {
        return "";
      }
      return result[1];
    }
    return "";
  };
  const orgName = extractOrgHandleFromPath(window.location.pathname);
  const getOrgHandle = (
    userReducer: UserReducer,
    organizationMetaDataReducer: OrganizationMetadataReducer
  ) => {
    if (Settings.isCustomDomain && organizationMetaDataReducer.success) {
      return organizationMetaDataReducer.organizationMetadata.handle;
    } else {
      if (userReducer.selectedOrg.handle) {
        return userReducer.selectedOrg.handle;
      } else {
        return extractOrgHandleFromPath(window.location.pathname);
      }
    }
  };

  const [organizationHandle, setOrganizationHandle] = useState<string>(
    getOrgHandle(userReducer, organizationMetadataReducer)
  );
  const redirectURL = organizationHandle
    ? "/" + organizationHandle
    : RESOURCE_SIGN_IN;
  const [messages, setMessages] = useState({});
  const locale = languageWithoutRegionCode || language;
  const [menuDrawerOpen, setMenuDrawerOpen] = useState(true);
  const [devportalTheme, setDevportalTheme] = useState<Theme>(systemTheme);

  const setThemeConfigs = async () => {
    try {
      const themeResponse = await axios.get(
        getAzureCDNEndpoint(organizationHandle),
        {
          headers: {
            "Cache-Control": "no-cache",
            Pragma: "no-cache",
            Expires: "0",
          },
        }
      );
      const {
        data: { palette, typography, assets, footerLinks, homePage, apiListingPage },
      } = themeResponse;

      const type = palette?.type;
      const background = {
        ...systemTheme?.palette?.background,
        paper:
          palette?.background?.primary[type] ||
          systemTheme?.palette?.background?.paper,
        default:
          palette?.background?.primary[type] ||
          systemTheme?.palette?.background?.default,
      };
      const text = {
        ...systemTheme?.palette?.text,
        primary:
          palette?.text?.primary[type] || systemTheme?.palette?.text?.primary,
        secondary:
          palette?.text?.secondary[type] ||
          systemTheme?.palette?.text?.secondary,
      };

      const primary = {
        ...systemTheme?.palette?.primary,
        main:
          palette?.background?.secondary[type] ||
          systemTheme?.palette?.primary?.main,
      };

      const secondary = {
        ...systemTheme?.palette?.primary,
        main:
          palette?.background?.secondary[type] ||
          systemTheme?.palette?.primary?.main,
      };

      const primaryColor = palette?.background?.primary[type];
      const secondaryColor = palette?.background?.secondary[type];
      const headingFont = typography?.headings?.fontFamily;
      const bodyFont = typography?.body?.fontFamily;
      const primaryTextColor = palette?.text?.primary[type];
      const secondaryTextColor = palette?.text?.secondary[type];
      const primaryBtnColor = palette?.button?.primary[type];
      const secondaryBtnColor = palette?.button?.secondary[type];

      const apiListingPageTitle = apiListingPage?.title ?? systemTheme?.custom?.apiListingPage?.title?.text;
      const apiListingPageSubTitle = apiListingPage?.subtitle ?? systemTheme?.custom?.apiListingPage?.subTitle?.text;
      const apiListingPageApiTilePrimaryColor = apiListingPage?.apiTilePalette?.background?.primary[type] 
        ?? systemTheme?.custom?.apiListingPage?.apiTilePalette?.background?.primary[type];
      const apiListingPageApiTileSecondaryColor = apiListingPage?.apiTilePalette?.background?.secondary[type] 
        ?? systemTheme?.custom?.apiListingPage?.apiTilePalette?.background?.secondary[type];
      const apiListingPageApiTilePrimaryTextColor = apiListingPage?.apiTilePalette?.text?.primary[type] 
        ?? systemTheme?.custom?.apiListingPage?.apiTilePalette?.text?.primary[type];
      const apiListingPageApiTileSecondaryTextColor = apiListingPage?.apiTilePalette?.text?.secondary[type] 
        ?? systemTheme?.custom?.apiListingPage?.apiTilePalette?.text?.secondary[type];
      const apiListingPageApiTilePrimaryBtnColor = apiListingPage?.apiTilePalette?.button?.primary[type] 
        ?? systemTheme?.custom?.apiListingPage?.apiTilePalette?.button?.primary[type];
      const apiListingPageApiTileSecondaryBtnColor = apiListingPage?.apiTilePalette?.button?.secondary[type] 
        ?? systemTheme?.custom?.apiListingPage?.apiTilePalette?.button?.secondary[type];

      const footerLinkItems = [
        {
          text: "Terms of Use",
          url: footerLinks?.terms,
        },
        {
          text: "Privacy Policy",
          url: footerLinks?.privacyPolicy,
        },
        {
          text: "Support",
          url: footerLinks?.support,
        },
      ];

      const custom = {
        ...systemTheme?.custom,
        login: {
          ...systemTheme?.custom.login,
          loginBg: {
            ...systemTheme?.custom.login.loginBg,
            backgroundColor: primaryColor,
          },
          logo: {
            ...systemTheme?.custom.login.logo,
            src: assets.logoUrl.header,
          },
          logoTitle: {
            ...systemTheme?.custom.login.logoTitle,
            color: primaryTextColor,
            fontFamily: headingFont,
          },
          successMsg: {
            ...systemTheme?.custom.login.successMsg,
            color: primaryTextColor,
            fontFamily: bodyFont,
          },
          divider: {
            ...systemTheme?.custom.login.divider,
            backgroundColor: primaryTextColor,
          },
          loginWrapper: {
            ...systemTheme?.custom.login.loginWrapper,
            backgroundColor: secondaryColor,
          },
          loginBtn: {
            ...systemTheme?.custom.login.loginBtn,
            backgroundColor: primaryBtnColor,
            "&:hover": {
              ...systemTheme?.custom.login.loginBtn["&:hover"],
              backgroundColor: secondaryBtnColor,
            },
          },
          google: {
            ...systemTheme?.custom.login.google,
            color: primaryTextColor,
            fontFamily: bodyFont,
          },
          github: {
            ...systemTheme?.custom.login.github,
            color: primaryTextColor,
            fontFamily: bodyFont,
          },
          microsoft: {
            ...systemTheme?.custom.login.microsoft,
            color: primaryTextColor,
            fontFamily: bodyFont,
          },
          tagLine: {
            ...systemTheme?.custom.login.tagLine,
            color: secondaryTextColor,
            fontFamily: headingFont,
          },
        },
        clientErrorPage: {
          ...systemTheme?.custom.clientErrorPage,
          signInText: {
            ...systemTheme?.custom.clientErrorPage.signInText,
            styles: {
              ...systemTheme?.custom.clientErrorPage.signInText.styles,
              fontFamily: headingFont,
            },
          },
        },
        appBar: {
          ...systemTheme?.custom.appBar,
          styles: {
            ...systemTheme?.custom.appBar.styles,
            backgroundColor: secondaryColor,
            color: secondaryTextColor,
          },
          logo: {
            ...systemTheme?.custom.appBar.logo,
            src: assets.logoUrl.header,
            styles: {
              ...systemTheme?.custom.appBar.logo.styles,
              maxWidth: "200px",
              maxHeight: "100px",
            },
          },
          title: {
            ...systemTheme?.custom.appBar.title,
            styles: {
              ...systemTheme?.custom.appBar.title.styles,
              color: secondaryTextColor,
            },
          },
          orgSelect: {
            ...systemTheme?.custom.appBar.orgSelect,
            color: secondaryTextColor,
          },
          menuButtons: {
            ...systemTheme?.custom.appBar.menuButtons,
            color: secondaryTextColor,
            opacity: .7,
          },
          menuButtonsSelected: {
            ...systemTheme?.custom.appBar.menuButtonsSelected,
            color: secondaryTextColor,
            filter:' contrast(500%);',
            opacity: 1,
          },
        },
        footer: {
          ...systemTheme?.custom.footer,
          logo: {
            ...systemTheme?.custom.footer.logo,
            src: assets.logoUrl.footer,
          },
          styles: {
            ...systemTheme?.custom.footer.styles,
            backgroundColor: secondaryColor,
            color: primaryTextColor,
          },
          loginFooter: {
            ...systemTheme?.custom.footer.loginFooter,
            backgroundColor: secondaryColor,
            color: primaryTextColor,
          },
          items: footerLinkItems,
        },
        homePage: {
          ...systemTheme?.custom.homePage,
          banner: {
            src: homePage?.banner,
          },
          browseButton: {
            ...systemTheme?.custom.homePage.browseButton,
            styles: {
              ...systemTheme?.custom.homePage.browseButton.styles,
              backgroundColor: primaryBtnColor,
              "&:hover": {
                ...systemTheme?.custom.homePage.browseButton.styles["&:hover"],
                backgroundColor: secondaryBtnColor,
              },
            }
          },
          sections: {
            ...systemTheme?.custom.homePage.sections,
            useCases: {
              ...systemTheme?.custom.homePage.sections.useCases,
              styles: {
                ...systemTheme?.custom.homePage.sections.useCases.styles,
                backgroundColor: secondaryColor,
              },
              title: {
                ...systemTheme?.custom.homePage.sections.useCases.title,
                styles: {
                  ...systemTheme?.custom.homePage.sections.useCases.title
                    .styles,
                  fontFamily: headingFont,
                  color: secondaryTextColor,
                },
              },
              subTitle: {
                ...systemTheme?.custom.homePage.sections.useCases.subTitle,
                styles: {
                  ...systemTheme?.custom.homePage.sections.useCases.subTitle
                    .styles,
                  fontFamily: bodyFont,
                  color: secondaryTextColor,
                },
              },
              cardStyles: {
                ...systemTheme?.custom.homePage.sections.useCases.cardStyles,
                title: {
                  ...systemTheme?.custom.homePage.sections.useCases.cardStyles
                    .title,
                  fontFamily: headingFont,
                  color: secondaryTextColor,
                },
                content: {
                  ...systemTheme?.custom.homePage.sections.useCases.cardStyles
                    .content,
                  fontFamily: bodyFont,
                  color: secondaryTextColor,
                },
              },
            },
            organization: {
              ...systemTheme?.custom.homePage.sections.organization,
              styles: {
                ...systemTheme?.custom.homePage.sections.organization.styles,
                backgroundColor: secondaryColor,
              },
              title: {
                ...systemTheme?.custom.homePage.sections.organization.title,
                styles: {
                  ...systemTheme?.custom.homePage.sections.organization.title
                    .styles,
                  fontFamily: headingFont,
                  color: secondaryTextColor,
                },
              },
              subTitle: {
                ...systemTheme?.custom.homePage.sections.organization.subTitle,
                styles: {
                  ...systemTheme?.custom.homePage.sections.organization.subTitle
                    .styles,
                  fontFamily: bodyFont,
                  color: secondaryTextColor,
                },
              },
              cardStyles: {
                ...systemTheme?.custom.homePage.sections.organization
                  .cardStyles,
                title: {
                  ...systemTheme?.custom.homePage.sections.organization
                    .cardStyles.title,
                  fontFamily: headingFont,
                  color: secondaryTextColor,
                },
                content: {
                  ...systemTheme?.custom.homePage.sections.organization
                    .cardStyles.content,
                  fontFamily: bodyFont,
                  color: secondaryTextColor,
                },
              },
            },
            developerBlog: {
              ...systemTheme?.custom.homePage.sections.developerBlog,
              styles: {
                ...systemTheme?.custom.homePage.sections.developerBlog.styles,
                backgroundColor: secondaryColor,
              },
              title: {
                ...systemTheme?.custom.homePage.sections.developerBlog.title,
                styles: {
                  ...systemTheme?.custom.homePage.sections.developerBlog.title
                    .styles,
                  fontFamily: headingFont,
                  color: secondaryTextColor,
                },
              },
              subTitle: {
                ...systemTheme?.custom.homePage.sections.developerBlog.subTitle,
                styles: {
                  ...systemTheme?.custom.homePage.sections.developerBlog
                    .subTitle.styles,
                  fontFamily: bodyFont,
                  color: secondaryTextColor,
                },
              },
              cardStyles: {
                ...systemTheme?.custom.homePage.sections.developerBlog
                  .cardStyles,
                title: {
                  ...systemTheme?.custom.homePage.sections.developerBlog
                    .cardStyles.title,
                  fontFamily: headingFont,
                  color: secondaryTextColor,
                },
                content: {
                  ...systemTheme?.custom.homePage.sections.developerBlog
                    .cardStyles.content,
                  fontFamily: bodyFont,
                  color: secondaryTextColor,
                },
                author: {
                  ...systemTheme?.custom.homePage.sections.developerBlog
                    .cardStyles.author,
                  fontFamily: bodyFont,
                  color: primaryTextColor,
                },
              },
            },
            introVideo: {
              ...systemTheme?.custom.homePage.sections.introVideo,
              title: {
                ...systemTheme?.custom.homePage.sections.introVideo.title,
                styles: {
                  ...systemTheme?.custom.homePage.sections.introVideo.title
                    .styles,
                  fontFamily: headingFont,
                  color: primaryTextColor,
                },
              },
              subTitle: {
                ...systemTheme?.custom.homePage.sections.introVideo.subTitle,
                styles: {
                  ...systemTheme?.custom.homePage.sections.introVideo.subTitle
                    .styles,
                  fontFamily: bodyFont,
                  color: primaryTextColor,
                },
              },
            },
            featuredApis: {
              ...systemTheme?.custom.homePage.sections.featuredApis,
              title: {
                ...systemTheme?.custom.homePage.sections.featuredApis.title,
                styles: {
                  ...systemTheme?.custom.homePage.sections.featuredApis.title
                    .styles,
                  fontFamily: headingFont,
                  color: primaryTextColor,
                },
              },
              subTitle: {
                ...systemTheme?.custom.homePage.sections.featuredApis.subTitle,
                styles: {
                  ...systemTheme?.custom.homePage.sections.featuredApis.subTitle
                    .styles,
                  fontFamily: bodyFont,
                  color: primaryTextColor,
                },
              },
            },
          },
          styles: {
            ...systemTheme?.custom.homePage.styles,
            backgroundColor: primaryColor,
          },
          title: {
            ...systemTheme?.custom.homePage.title,
            text: homePage?.title,
            styles: {
              ...systemTheme?.custom.homePage.title.styles,
              fontFamily: headingFont,
              color: primaryTextColor,
            },
          },
          subTitle: {
            ...systemTheme?.custom.homePage.subTitle,
            text: homePage?.subtitle,
            styles: {
              ...systemTheme?.custom.homePage.subTitle.styles,
              fontFamily: bodyFont,
              color: primaryTextColor,
            },
          },
        },
        apiListingPage: {
          ...systemTheme?.custom.apiListingPage,
          styles: {
            ...systemTheme?.custom.apiListingPage.styles,
            backgroundColor: primaryColor,
          },
          title: {
            ...systemTheme?.custom.apiListingPage.title,
            text: apiListingPageTitle,
            styles: {
              ...systemTheme?.custom.apiListingPage.title.styles,
              fontFamily: headingFont,
              color: primaryTextColor,
            },
          },
          subTitle: {
            ...systemTheme?.custom.apiListingPage.subTitle,
            text: apiListingPageSubTitle,
            styles: {
              ...systemTheme?.custom.apiListingPage.subTitle.styles,
              fontFamily: bodyFont,
              color: primaryTextColor,
            },
          },
          tagCategory: {
            ...systemTheme?.custom.apiListingPage.tagCategory,
            styles: {
              ...systemTheme?.custom.apiListingPage.tagCategory.styles,
              fontFamily: bodyFont,
              color: primaryTextColor,
            },
          },
          tagChip: {
            ...systemTheme?.custom.apiListingPage.tagChip,
            backgroundColor: secondaryColor,
          },
          apiSearchBar: {
            ...systemTheme?.custom.apiListingPage.apiSearchBar,
            searchButton: {
              ...systemTheme?.custom.apiListingPage.apiSearchBar.searchButton,
              backgroundColor: primaryBtnColor,
              "&:hover": {
                ...systemTheme?.custom.apiListingPage.apiSearchBar.searchButton["&:hover"],
                backgroundColor: secondaryBtnColor,
              },
            },
          },
        },
        appListingPage: {
          ...systemTheme?.custom.appListingPage,
          styles: {
            ...systemTheme?.custom.appListingPage.styles,
            backgroundColor: primaryColor,
          },
          createBtn: {
            ...systemTheme?.custom.appListingPage.createBtn,
            backgroundColor: primaryBtnColor,
            "&:hover": {
              ...systemTheme?.custom.appListingPage.createBtn["&:hover"],
              backgroundColor: secondaryBtnColor,
            },
          },
          title: {
            ...systemTheme?.custom.appListingPage.title,
            fontFamily: headingFont,
            color: primaryTextColor,
          },
          subTitle: {
            ...systemTheme?.custom.appListingPage.subTitle,
            fontFamily: bodyFont,
            color: primaryTextColor,
          },
          listHeaderTextStyle: {
            ...systemTheme?.custom.appListingPage.listHeaderTextStyle,
            fontFamily: bodyFont,
            color: primaryTextColor,
          },
          defaultAppThumbnail: {
            ...systemTheme?.custom.appListingPage.defaultAppThumbnail,
            backgroundColor: secondaryColor,
          },
          appSearchFieldStyle: {
            ...systemTheme?.custom.appListingPage.appSearchFieldStyle,
            "& .MuiTextField-root": {
              ...systemTheme?.custom.appListingPage.appSearchFieldStyle[
                "& .MuiTextField-root"
              ],
              "& input::placeholder": {
                ...systemTheme?.custom.appListingPage.appSearchFieldStyle[
                  "& .MuiTextField-root"
                ]["& input::placeholder"],
                color: primaryTextColor,
              },
            },
          },
        },
        apiOverviewPage: {
          ...systemTheme?.custom.apiOverviewPage,
          leftMenu: {
            ...systemTheme?.custom.apiOverviewPage.leftMenu,
            styles: {
              ...systemTheme?.custom.apiOverviewPage.leftMenu.styles,
              backgroundColor: secondaryColor,
              color: secondaryTextColor,
            },
            menuItems: {
              ...systemTheme?.custom.apiOverviewPage.leftMenu.menuItems,
              color: secondaryTextColor,
              selected: {
                ...systemTheme?.custom.apiOverviewPage.leftMenu.menuItems
                  .selected,
                color: secondaryTextColor,
                filter:' contrast(200%);',
                opacity: 1,
              },
            },
          },
          tabs: {
            ...systemTheme?.custom.apiOverviewPage.tabs,
            overview: {
              ...systemTheme?.custom.apiOverviewPage.tabs.overview,
              styles: {
                ...systemTheme?.custom.apiOverviewPage.tabs.overview.styles,
                backgroundColor: primaryColor,
              },
              apiIconImage: {
                ...systemTheme?.custom.apiOverviewPage.tabs.overview
                  .apiIconImage,
                backgroundColor: secondaryColor,
              },
              apiName: {
                ...systemTheme?.custom.apiOverviewPage.tabs.overview.apiName,
                color: primaryTextColor,
                fontFamily: headingFont,
              },
              apiDetailTitles: {
                ...systemTheme?.custom.apiOverviewPage.tabs.overview
                  .apiDetailTitles,
                color: primaryTextColor,
              },
              apiDetailsContent: {
                ...systemTheme?.custom.apiOverviewPage.tabs.overview
                  .apiDetailsContent,
                color: primaryTextColor,
              },
              apiDocumentsTitleText: {
                ...systemTheme?.custom.apiOverviewPage.tabs.overview
                  .apiDocumentsTitleText,
                color: primaryTextColor,
              },
              apiTagsTitleText: {
                ...systemTheme?.custom.apiOverviewPage.tabs.overview
                  .apiTagsTitleText,
                color: primaryTextColor,
              },
            },
            credentials: {
              ...systemTheme?.custom.apiOverviewPage.tabs.credentials,
              styles: {
                ...systemTheme?.custom.apiOverviewPage.tabs.credentials.styles,
                backgroundColor: primaryColor,
              },
              subTitle: {
                ...systemTheme?.custom.apiOverviewPage.tabs.credentials
                  .subTitle,
                color: primaryTextColor,
              },
              helperTextStyle: {
                ...systemTheme?.custom.apiOverviewPage.tabs.credentials
                  .helperTextStyle,
                color: primaryTextColor,
              },
              updateButton: {
                ...systemTheme?.custom.apiOverviewPage.tabs.credentials.updateButton,
                backgroundColor: primaryBtnColor,
                "&:hover": {
                  ...systemTheme?.custom.apiOverviewPage.tabs.credentials.updateButton["&:hover"],
                  backgroundColor: secondaryBtnColor,
                },
              },
              generateCredentialsButton: {
                ...systemTheme?.custom.apiOverviewPage.tabs.credentials.generateCredentialsButton,
                backgroundColor: primaryBtnColor,
                "&:hover": {
                  ...systemTheme?.custom.apiOverviewPage.tabs.credentials.generateCredentialsButton["&:hover"],
                  backgroundColor: secondaryBtnColor,
                },
              }
            },
            tryout: {
              ...systemTheme?.custom.apiOverviewPage.tabs.tryout,
              styles: {
                ...systemTheme?.custom.apiOverviewPage.tabs.tryout.styles,
                backgroundColor: primaryColor,
              },
              textLabel: {
                ...systemTheme?.custom.apiOverviewPage.tabs.tryout.subTitle,
                color: primaryTextColor,
              },
              applicationDropdownStyle: {
                ...systemTheme?.custom.apiOverviewPage.tabs.tryout
                  .helperTextStyle,
                color: primaryTextColor,
              },
            },
            documentation: {
              ...systemTheme?.custom.apiOverviewPage.tabs.documentation,
              styles: {
                ...systemTheme?.custom.apiOverviewPage.tabs.documentation
                  .styles,
                backgroundColor: primaryColor,
              },
              documentContentStyle: {
                ...systemTheme?.custom.apiOverviewPage.tabs.documentation
                  .documentContentStyle,
                backgroundColor: primaryColor,
              },
              applicationDropdownStyle: {
                ...systemTheme?.custom.apiOverviewPage.tabs.documentation
                  .helperTextStyle,
                backgroundColor: primaryColor,
              },
              documentTypeTextStyle: {
                ...systemTheme?.custom.apiOverviewPage.tabs.documentation
                  .documentTypeTextStyle,
                color: primaryTextColor,
              },
            },
            sdks: {
              ...systemTheme?.custom.apiOverviewPage.tabs.sdks,
              styles: {
                ...systemTheme?.custom.apiOverviewPage.tabs.sdks.styles,
                backgroundColor: primaryColor,
              },
              SDKCardStyle: {
                ...systemTheme?.custom.apiOverviewPage.tabs.sdks.SDKCardStyle,
                backgroundColor: primaryColor,
              },
              title: {
                ...systemTheme?.custom.apiOverviewPage.tabs.sdks.title,
                color: primaryTextColor,
              },
              SDKLanguageStyle: {
                ...systemTheme?.custom.apiOverviewPage.tabs.sdks
                  .SDKLanguageStyle,
                color: primaryTextColor,
              },
            },
            contracts: {
              ...systemTheme?.custom.apiOverviewPage.tabs.contracts,
              styles: {
                ...systemTheme?.custom.apiOverviewPage.tabs.contracts.styles,
                backgroundColor: primaryColor,
              },
              title: {
                ...systemTheme?.custom.apiOverviewPage.tabs.contracts.title,
                color: primaryTextColor,
              },
              updateButtonStyle: {
                ...systemTheme?.custom.apiOverviewPage.tabs.contracts.updateButtonStyle,
              },
              updateButton: {
                ...systemTheme?.custom.apiOverviewPage.tabs.contracts.updateButton,
                backgroundColor: primaryBtnColor,
                "&:hover": {
                  ...systemTheme?.custom.apiOverviewPage.tabs.contracts.updateButton["&:hover"],
                  backgroundColor: secondaryBtnColor,
                },
              }
            },
          },
        },
        applicationViewPage: {
          ...systemTheme?.custom.applicationViewPage,
          tabs: {
            ...systemTheme?.custom.applicationViewPage.tabs,
            overview: {
              ...systemTheme?.custom.applicationViewPage.tabs.overview,
              styles: {
                ...systemTheme?.custom.applicationViewPage.tabs.overview.styles,
                backgroundColor: primaryColor,
              },
              overviewRowValueTextStyle: {
                ...systemTheme?.custom.applicationViewPage.tabs.overview
                  .overviewRowValueTextStyle,
                color: primaryTextColor,
              },
              overviewRowKeyTextStyle: {
                ...systemTheme?.custom.applicationViewPage.tabs.overview
                  .overviewRowKeyTextStyle,
                color: primaryTextColor,
              },
            },
            subscriptions: {
              ...systemTheme?.custom.applicationViewPage.tabs.subscriptions,
              styles: {
                ...systemTheme?.custom.applicationViewPage.tabs.subscriptions
                  .styles,
                backgroundColor: primaryColor,
              },
              pageTitle: {
                ...systemTheme?.custom.applicationViewPage.tabs.subscriptions
                  .pageTitle,
                color: primaryTextColor,
              },
              addApiBtn: {
                ...systemTheme?.custom.applicationViewPage.tabs.subscriptions.addApiBtn,
                backgroundColor: primaryBtnColor,
                "&:hover": {
                  ...systemTheme?.custom.applicationViewPage.tabs.subscriptions.addApiBtn["&:hover"],
                  backgroundColor: secondaryBtnColor,
                },
              },
              copyScopesBtn: {
                ...systemTheme?.custom.applicationViewPage.tabs.subscriptions.copyScopesBtn,
                backgroundColor: primaryBtnColor,
                "&:hover": {
                  ...systemTheme?.custom.applicationViewPage.tabs.subscriptions.copyScopesBtn["&:hover"],
                  backgroundColor: secondaryBtnColor,
                },
              },
            },
            productionKeys: {
              ...systemTheme?.custom.applicationViewPage.tabs.productionKeys,
              oAuth: {
                ...systemTheme?.custom.applicationViewPage.tabs.productionKeys
                  .oAuth,
                styles: {
                  ...systemTheme?.custom.applicationViewPage.tabs.productionKeys
                    .oAuth.styles,
                  backgroundColor: primaryColor,
                },
                updateAdvancedConfigurationsButtonStyles: {
                  ...systemTheme?.custom.applicationViewPage.tabs.productionKeys
                  .oAuth.updateAdvancedConfigurationsButtonStyles,
                  backgroundColor: primaryBtnColor,
                  "&:hover": {
                    ...systemTheme?.custom.applicationViewPage.tabs.productionKeys
                  .oAuth.updateAdvancedConfigurationsButtonStyles["&:hover"],
                    backgroundColor: secondaryBtnColor,
                  },
                },
                subTitleTextStyles: {
                  ...systemTheme?.custom.applicationViewPage.tabs.productionKeys
                    .oAuth.subTitleTextStyles,
                  color: primaryTextColor,
                },
                contentKeyColor: {
                  ...systemTheme?.custom.applicationViewPage.tabs.productionKeys
                    .oAuth.contentKeyColor,
                  color: primaryTextColor,
                },
                advancedConfigKeyStyle: {
                  ...systemTheme?.custom.applicationViewPage.tabs.productionKeys
                    .oAuth.advancedConfigKeyStyle,
                  color: primaryTextColor,
                },
              },
            },
          },
        },
        elements: {
          ...systemTheme?.custom.elements,
          apiCard: {
            ...systemTheme?.custom.elements.apiCard,
            styles: {
              ...systemTheme?.custom.elements.apiCard.styles,
              backgroundColor: apiListingPageApiTilePrimaryColor,
              "&:hover": {
                ...systemTheme?.custom.elements.apiCard.styles["&:hover"],
                backgroundColor: apiListingPageApiTilePrimaryColor,
                borderColor: apiListingPageApiTileSecondaryColor,
                boxShadow: `0px 0px 15px -10px ${apiListingPageApiTileSecondaryColor}`
              },
            },
            apiType: {
              ...systemTheme?.custom.elements.apiCard.apiType,
              color: apiListingPageApiTileSecondaryTextColor,
            },
            lifecycleStatus: {
              ...systemTheme?.custom.elements.apiCard.lifecycleStatus,
              color: apiListingPageApiTilePrimaryTextColor,
            },
            versionWrapper: {
              ...systemTheme?.custom.elements.apiCard.versionWrapper,
              color: apiListingPageApiTileSecondaryTextColor,
            },
            rating: {
              ...systemTheme?.custom.elements.apiCard.rating,
              color: apiListingPageApiTileSecondaryColor,
            },
            tryOutButton: {
              ...systemTheme?.custom.elements.apiCard.tryOutButton,
              backgroundColor: apiListingPageApiTilePrimaryBtnColor,
              color: apiListingPageApiTilePrimaryTextColor,
              "&:hover": {
                ...systemTheme?.custom.elements.apiCard.tryOutButton["&:hover"],
                backgroundColor: apiListingPageApiTileSecondaryBtnColor,
                fontWeight: 'bold',
              }
            },
            docsButton: {
              ...systemTheme?.custom.elements.apiCard.docsButton,
              backgroundColor: apiListingPageApiTilePrimaryColor,
              color: apiListingPageApiTilePrimaryTextColor,
              "&:hover": {
                ...systemTheme?.custom.elements.apiCard.tryOutButton["&:hover"],
                backgroundColor: apiListingPageApiTilePrimaryColor,
                fontWeight: 'bold',
              }
            },
            icon: {
              ...systemTheme?.custom.elements.apiCard.icon,
              backgroundColor: apiListingPageApiTileSecondaryColor,
              color: apiListingPageApiTilePrimaryColor,
            },
            content: {
              ...systemTheme?.custom.elements.apiCard.content,
              color: apiListingPageApiTileSecondaryTextColor,
            },
            title: {
              ...systemTheme?.custom.elements.apiCard.title,
              color: apiListingPageApiTilePrimaryTextColor,
            },
          },
          apiInfoBar: {
            ...systemTheme?.custom.elements.apiInfoBar,
            apiIconImage: {
              ...systemTheme?.custom.elements.apiInfoBar.apiIconImage,
              backgroundColor: secondaryColor,
            },
            apiName: {
              ...systemTheme?.custom.elements.apiInfoBar.apiName,
              color: primaryTextColor,
              fontFamily: headingFont,
            },
          },
          appInfoBar: {
            ...systemTheme?.custom.elements.appInfoBar,
            appIconImage: {
              ...systemTheme?.custom.elements.appInfoBar.appIconImage,
              backgroundColor: secondaryColor,
            },
            appNameStyles: {
              ...systemTheme?.custom.elements.appInfoBar.apiName,
              color: primaryTextColor,
              fontFamily: headingFont,
            },
          },
        },
      };

      const modifiedTheme = {
        ...systemTheme,
        palette: {
          ...systemTheme?.palette,
          type,
          background,
          text,
          primary,
          secondary,
        },
        typography: {
          ...systemTheme?.typography,
          ...typography,
          fontFamily: typography?.body?.fontFamily,
        },
        custom: {
          ...systemTheme?.custom,
          ...custom,
        },
      };
      setDevportalTheme(modifiedTheme);
      const link = document.querySelector(
        "link[rel~='icon']"
      ) as HTMLAnchorElement | null;
      if (link !== null) {
        link.href = assets.logoUrl.favicon;
      }
    } catch (err) {
      setDevportalTheme(systemTheme);
    }
  };

  useEffect(() => {
    if (Settings.isCustomDomain) {
      setOrganizationHandle(
          organizationMetadataReducer.organizationMetadata.handle
      );
    } else {
      setOrganizationHandle(userReducer.selectedOrg.handle);
    }
  }, [organizationMetadataReducer, userReducer]);

  useEffect(() => {
    if (Settings.isCustomDomain) {
      fetchOrganizationMetadata('', Settings.customDomain);
    } else if (orgName && orgName != localStoreOperation(StorageOperations.GET, SELECTED_ORG_HANDLE_NAME)) {
      fetchOrganizationMetadata(orgName);
    }
  }, [orgName]);


  useEffect(() => {
    // load locale unless the language is en
    if (locale !== "en") {
      fetch(`/lang/${locale}.json`)
        .then((resp) => resp.json())
        .then((msgs) => setMessages(msgs));
    }
    if (organizationHandle) {
      setThemeConfigs();
    }
  }, [organizationHandle]);

  const RenderAppRoutes = (redirectURL: string) => {
    return (
      <ThemeProvider theme={devportalTheme}>
        <SnackbarProvider
          maxSnack={4}
          anchorOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          ref={notistackRef}
          preventDuplicate
          action={(key) => (
            <Button onClick={onClickDismiss(key)}>
              <CloseIcon fontSize="small" htmlColor="white" />
            </Button>
          )}
        >
          <IntlProvider messages={messages} locale={locale} defaultLocale="en">
            <QueryClientProvider client={queryClient}>
              <Box className="App">
                <Switch>
                  <Route exact path={"/"}>
                    <Redirect to={redirectURL} />
                  </Route>
                  <Route path={["/apis", "/applications"]}>
                    <PageNotFound />
                  </Route>
                  <Route path={"/sign-in"}>
                    <SignIn />
                  </Route>
                  <Route path={"/login"}>
                    <Login />
                  </Route>
                  <Route path={"/accept-invitation"}>
                    <AcceptInvitation />
                  </Route>
                  <Route path={"/internal-error"}>
                    <InternalServerError />
                  </Route>
                  <Route path={"/forbidden"}>
                    <Forbidden/>
                  </Route>
                  <Route path={"/client-error"}>
                    <ClientError />
                  </Route>
                  <Route
                    exact={true}
                    path={["/unauthorized", "/unauthorized/:error_code"]}
                  >
                    <Unauthorized />
                  </Route>
                  <Route exact path={"/:orgName"}>
                    <MainAppBar
                      menuDrawerOpen={menuDrawerOpen}
                      setMenuDrawerOpen={setMenuDrawerOpen}
                    />
                    <DevportalHome />
                  </Route>
                  <Route exact path={"/:orgName/apis"}>
                    <MainAppBar
                      menuDrawerOpen={menuDrawerOpen}
                      setMenuDrawerOpen={setMenuDrawerOpen}
                    />
                    <ApiListPage />
                  </Route>
                  <Route exact path={"/:orgName/apis/:apiId"}>
                    <MainAppBar
                      menuDrawerOpen={menuDrawerOpen}
                      setMenuDrawerOpen={setMenuDrawerOpen}
                      showDrawerButton={true}
                    />
                    <ApiPage menuDrawerOpen={menuDrawerOpen} />
                  </Route>
                  <Route
                    path={
                      "/:orgName/apis/:apiId/:menu(overview|credentials|tryout|resources|contracts|documents|sdks)"
                    }
                  >
                    <MainAppBar
                      menuDrawerOpen={menuDrawerOpen}
                      setMenuDrawerOpen={setMenuDrawerOpen}
                      showDrawerButton={true}
                    />
                    <ApiPage menuDrawerOpen={menuDrawerOpen} />
                  </Route>
                  <Route exact path={"/:orgName/applications"}>
                    <MainAppBar
                      menuDrawerOpen={menuDrawerOpen}
                      setMenuDrawerOpen={setMenuDrawerOpen}
                      showDrawerButton={false}
                    />
                    <AppList />
                  </Route>
                  <Route
                    path={[
                      "/:orgName/applications/:applicationId/edit-subscription/:subscriptionId",
                      "/:orgName/applications/:applicationId",
                      "/:orgName/applications/:applicationId/:menu(overview|productionkeys|subscriptions|new-subscriptions)",
                      "/:orgName/applications/:applicationId/productionkeys/:keyType"
                    ]}
                  >
                    <MainAppBar
                      menuDrawerOpen={menuDrawerOpen}
                      setMenuDrawerOpen={setMenuDrawerOpen}
                      showDrawerButton={true}
                    />
                    <AppPage menuDrawerOpen={menuDrawerOpen} />
                  </Route>
                  <Route path={"*"}>
                    <PageNotFound />
                  </Route>
                </Switch>
              </Box>
            </QueryClientProvider>
          </IntlProvider>
        </SnackbarProvider>
      </ThemeProvider>
    );
  };

  const notistackRef: any = React.createRef();
  const onClickDismiss = (key: any) => () => {
    notistackRef.current.closeSnackbar(key);
  };
  /* Temporary removed the hotjar tracking function due to a GDPR security concerns */
  // useHotJarIdentification();

  if (Settings.isCustomDomain) {
    if (organizationMetadataReducer.loading) {
      return (
        <div className={classes.loaderContainer}>
          <PreLoaderLogo size="medium" />
        </div>
      );
    } else if (organizationMetadataReducer.success) {
      return RenderAppRoutes(organizationHandle);
    }
  } else {
    if (organizationMetadataReducer.loading) {
      return (
          <div className={classes.loaderContainer}>
            <PreLoaderLogo size="medium" />
          </div>
      );
    } else if (organizationMetadataReducer.success) {
      return RenderAppRoutes("/" + orgName);
    } else {
      return RenderAppRoutes(redirectURL);
    }
  }
  return null;
}

const mapStateToProps = (state: ApplicationState) => {
  return {
    userReducer: state.userReducer,
    organizationMetadataReducer: state.organizationMetadataReducer,
    customDomainOrganizationMappingReducer:
      state.customDomainOrganizationMappingReducer,
  };
};

const mapDispatchToProps = {
  fetchCustomDomainOrganizationMapping: fetchCustomDomainOrganizationMapping,
  fetchOrganizationMetadata: fetchOrganizationMetadata,
};

export default connect(mapStateToProps, mapDispatchToProps)(App);
